import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// SaaS Pages
import SaaSLanding from "./pages/SaaSLandingNew";
import EmpresaLanding from "./pages/EmpresaLanding";
import EmpresaLogin from "./pages/EmpresaLogin";
import EmpresaDenunciaForm from "./pages/EmpresaDenunciaForm";
import EmpresaOuvidoriaForm from "./pages/EmpresaOuvidoriaForm";
import EmpresaAcompanhamento from "./pages/EmpresaAcompanhamento";
import OuvidoriaEsquecimento from "./pages/OuvidoriaEsquecimento";
import EmpresaDashboard from "./pages/EmpresaDashboard";
import EmpresaCasos from "./pages/EmpresaCasos";
import EmpresaConfiguracoes from "./pages/EmpresaConfiguracoes";
import InvestigadoresPage from "./pages/InvestigadoresPage";
import EmailTemplatesPage from "./pages/EmailTemplatesPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancelled from "./pages/PaymentCancelled";
import OnboardingUnified from "./pages/OnboardingUnified";

// Super Admin Pages
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";

// Institutional Pages
import SobrePage from "./pages/SobrePage";
import ContatoPage from "./pages/ContatoPage";
import PrivacidadePage from "./pages/PrivacidadePage";
import TermosPage from "./pages/TermosPage";
import CodigoEticaPage from "./pages/CodigoEticaPage";
import GuidesHub from "./pages/GuidesHub";
import GuideArticle from "./pages/GuideArticle";
import BlogPage from "./pages/BlogPage";
import BlogArticle from "./pages/BlogArticle";
import SolucaoBrasilPage from "./pages/SolucaoBrasilPage";
import SolucaoBrasilOuvidoriaPage from "./pages/SolucaoBrasilOuvidoriaPage";
import SolucaoBrasilCompliancePage from "./pages/SolucaoBrasilCompliancePage";

// Legacy Pages (for backward compatibility)
import LandingPage from "./pages/LandingPage";
import DenunciaForm from "./pages/DenunciaForm";
import DenunciaConfirmacao from "./pages/DenunciaConfirmacao";
import DenunciaAcompanhamento from "./pages/DenunciaAcompanhamento";
import OuvidoriaForm from "./pages/OuvidoriaForm";
import OuvidoriaConfirmacao from "./pages/OuvidoriaConfirmacao";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import CasesList from "./pages/CasesList";
import CaseDetails from "./pages/CaseDetails";
import UsersManagement from "./pages/UsersManagement";
import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";

// Context
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Onboarding - Unified for all plans */}
          <Route path="/onboarding" element={<OnboardingUnified />} />
          <Route path="/onboarding/empresa" element={<OnboardingUnified />} />
          <Route path="/onboarding/free" element={<OnboardingUnified />} />
          
          {/* SaaS Landing - Main Entry Point */}
          <Route path="/" element={<SaaSLanding />} />
          
          {/* Super Admin Routes */}
          <Route path="/super/login" element={<SuperAdminLogin />} />
          <Route path="/super/dashboard" element={<SuperAdminDashboard />} />
          
          {/* Institutional Pages - Portuguese */}
          <Route path="/pt/sobre" element={<SobrePage />} />
          <Route path="/pt/contato" element={<ContatoPage />} />
          <Route path="/pt/politica-de-privacidade" element={<PrivacidadePage />} />
          <Route path="/pt/termos-de-uso" element={<TermosPage />} />
          <Route path="/pt/codigo-de-etica" element={<CodigoEticaPage />} />
          <Route path="/pt/guias" element={<GuidesHub />} />
          <Route path="/pt/guias/:slug" element={<GuideArticle />} />
          <Route path="/pt/blog" element={<BlogPage />} />
          <Route path="/pt/blog/:slug" element={<BlogArticle />} />
          
          {/* SEO Landing Pages */}
          <Route path="/solucaoBrasil" element={<SolucaoBrasilPage />} />
          <Route path="/solucaobrasilouvidoria" element={<SolucaoBrasilOuvidoriaPage />} />
          <Route path="/solucaobrasilcompliance" element={<SolucaoBrasilCompliancePage />} />
          
          {/* Institutional Pages - English */}
          <Route path="/en/about" element={<SobrePage />} />
          <Route path="/en/contact" element={<ContatoPage />} />
          <Route path="/en/privacy-policy" element={<PrivacidadePage />} />
          <Route path="/en/terms-of-use" element={<TermosPage />} />
          <Route path="/en/code-of-ethics" element={<CodigoEticaPage />} />
          <Route path="/en/guides" element={<GuidesHub />} />
          <Route path="/en/guides/:slug" element={<GuideArticle />} />
          <Route path="/en/blog" element={<BlogPage />} />
          <Route path="/en/blog/:slug" element={<BlogArticle />} />
          
          {/* Language-prefixed home routes */}
          <Route path="/pt" element={<SaaSLanding />} />
          <Route path="/en" element={<SaaSLanding />} />
          
          {/* Payment Routes */}
          <Route path="/pagamento/sucesso" element={<PaymentSuccess />} />
          <Route path="/pagamento/cancelado" element={<PaymentCancelled />} />
          
          {/* Multi-tenant Routes */}
          <Route path="/:slug" element={<EmpresaLanding />} />
          <Route path="/:slug/login" element={<EmpresaLogin />} />
          <Route path="/:slug/denuncia" element={<EmpresaDenunciaForm />} />
          <Route path="/:slug/ouvidoria" element={<EmpresaOuvidoriaForm />} />
          <Route path="/:slug/ouvidoria/esquecimento" element={<OuvidoriaEsquecimento />} />
          <Route path="/:slug/acompanhar" element={<EmpresaAcompanhamento />} />
          <Route path="/:slug/admin" element={<EmpresaDashboard />} />
          <Route path="/:slug/admin/casos" element={<EmpresaCasos />} />
          <Route path="/:slug/admin/denuncias/:id" element={<CaseDetails type="denuncia" multiTenant />} />
          <Route path="/:slug/admin/ouvidoria/:id" element={<CaseDetails type="ouvidoria" multiTenant />} />
          <Route path="/:slug/admin/investigadores" element={<InvestigadoresPage />} />
          <Route path="/:slug/admin/configuracoes" element={<EmpresaConfiguracoes />} />
          <Route path="/:slug/admin/emails" element={<EmailTemplatesPage />} />
          
          {/* Legacy Routes (backward compatibility) */}
          <Route path="/legacy" element={<LandingPage />} />
          <Route path="/legacy/denuncia" element={<DenunciaForm />} />
          <Route path="/legacy/denuncia/confirmacao" element={<DenunciaConfirmacao />} />
          <Route path="/legacy/denuncia/acompanhar" element={<DenunciaAcompanhamento />} />
          <Route path="/legacy/ouvidoria" element={<OuvidoriaForm />} />
          <Route path="/legacy/ouvidoria/confirmacao" element={<OuvidoriaConfirmacao />} />
          <Route path="/legacy/login" element={<LoginPage />} />
          <Route path="/legacy/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/legacy/admin/casos" element={<ProtectedRoute><CasesList /></ProtectedRoute>} />
          <Route path="/legacy/admin/denuncias/:id" element={<ProtectedRoute><CaseDetails type="denuncia" /></ProtectedRoute>} />
          <Route path="/legacy/admin/ouvidoria/:id" element={<ProtectedRoute><CaseDetails type="ouvidoria" /></ProtectedRoute>} />
          <Route path="/legacy/admin/usuarios" element={<ProtectedRoute requiredRoles={["admin"]}><UsersManagement /></ProtectedRoute>} />
          
          {/* Common Routes */}
          <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
